<template>
  <div class="main_profile_wrapper">
    <div v-if="!canAccessModule" class="p-4">
      <el-alert
        :title="localization('Access denied')"
        :description="localization('You do not have permission to access Strategies Card.')"
        type="warning"
        show-icon
      />
    </div>

    <div v-else class="main_table">
      <TableHeaderSection
        :title="`${localization('Strategies Card')}`"
        :text="`${localization('Browse and manage your strategies cards.')}`"
        :total="totalStrategiesCards"
      ></TableHeaderSection>

      <div class="header_container just_custom_table_tasks">
        <server-table
          ref="customTable"
          :count="totalStrategiesCards"
          :DataItems="strategiesCards"
          @Refresh="Refresh"
          @reset="resetFilters"
          :filter="filter"
          :title="'strategies_card'"
          :loading="loading"
          hidden_export_import
          :action="canCreate || canImport"
          :edit_permission="importDropdownPermission"
        >
          <template #filter >
            <div class="d-flex flex-wrap filters_gap" style="">
              <el-select
                v-model="filter.topic_id"
                clearable
                filterable
                class="filter_select"
                :placeholder="`${localization('Topic')}`"
                @change="handleFilterTopicChange"
              >
                <el-option
                  v-for="item in topicsCardLookups"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>

              <el-select
                v-model="filter.subtopic_id"
                clearable
                filterable
                class="filter_select"
                :disabled="!filter.topic_id"
                :placeholder="`${localization('Subtopic')}`"
              >
                <el-option
                  v-for="item in filterSubTopicOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>

              <el-select
                v-model="filter.language_id"
                clearable
                filterable
                class="filter_select"
                :placeholder="`${localization('Language')}`"
              >
                <el-option
                  v-for="item in languageOptions"
                  :key="item.id"
                  :label="getLanguageLabel(item)"
                  :value="item.id"
                />
              </el-select>

              <el-select
                v-model="filter.classification"
                clearable
                filterable
                class="filter_select"
                :placeholder="`${localization('Classification')}`"
              >
                <el-option
                  v-for="item in classificationOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>

              <el-select
                v-model="filter.pattern"
                clearable
                filterable
                class="filter_select"
                :placeholder="`${localization('Pattern')}`"
              >
                <el-option
                  v-for="item in patternOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>

              <el-select
                v-model="filter.age_group"
                clearable
                filterable
                class="filter_select"
                :placeholder="`${localization('Age Group')}`"
              >
                <el-option
                  v-for="item in ageGroupOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
            </div>
          </template>

          <template #columns>
            <el-table-column prop="name" :label="`${localization('Name')}`">
              <template #default="{ row }">
                <div v-html="row.name || '-'"></div>
              </template>
            </el-table-column>
            <el-table-column prop="classification" :label="`${localization('Classification')}`"  />
            <el-table-column prop="pattern" :label="`${localization('Pattern')}`"  />
            <el-table-column prop="priority" :label="`${localization('Priority')}`"  />
            <el-table-column prop="age_group" :label="`${localization('Age Group')}`"  />
            <el-table-column prop="language_name" :label="`${localization('Language')}`"  />
            <el-table-column prop="topic_name" :label="`${localization('Topic')}`"  />
            <el-table-column prop="subtopic_name" :label="`${localization('Subtopic')}`"  />
            <el-table-column prop="description" :label="`${localization('Description')}`">
              <template #default="{ row }">
                <div v-html="row.description || '-'"></div>
              </template>
            </el-table-column>
            <el-table-column :label="`${localization('Actions')}`" width="180" align="left" header-align="left">
              <template slot-scope="scope">
                <div class="td-actions">
                  <span>
                    <a
                      v-if="canView"
                      v-tooltip.top-center="`${localization('Show')}`"
                      @click="showDetails(scope.row.id)"
                    >
                      <img
                        role="button"
                        src="@/assets/new-theme/eye.png"
                        alt="eye"
                        class="mx-2"
                      />
                    </a>
                    <a
                      v-if="canEdit && !scope.row.deleted_at"
                      v-tooltip.top-center="`${localization('Edit')}`"
                      @click="openEditModal(scope.row)"
                    >
                      <img
                        role="button"
                        src="@/assets/new-theme/edit.png"
                        alt="edit"
                        class="mr-2"
                      />
                    </a>
                    <a
                      v-if="canDelete && !scope.row.deleted_at"
                      v-tooltip.top-center="`${localization('Delete')}`"
                      @click="deleteStrategyCard(scope.row.id)"
                    >
                      <img
                        role="button"
                        src="@/assets/new-theme/delete-button-table.png"
                        alt="delete"
                      />
                    </a>
                    <a
                      v-if="canEdit && scope.row.deleted_at"
                      v-tooltip.top-center="`${localization('Restore')}`"
                      @click="restoreStrategyCard(scope.row.id)"
                    >
                      <img
                        role="button"
                        src="@/assets/images/icons/reverse-left.png"
                        alt="restore"
                        class="restore_icon"
                      />
                    </a>
                  </span>
                </div>
              </template>
            </el-table-column>
          </template>
          <template #action>
            <div v-if="canCreate" @click="openCreateModal">
              <el-icon class="el-icon-plus"></el-icon>
              {{ localization("Create") }}
            </div>
          </template>
          <template #another_action>
            <div v-if="canImport" @click="openImportDialog">
              <el-icon class="el-icon-upload"></el-icon>
              {{ localization("Import") }}
            </div>
          </template>
        </server-table>
      </div>
    </div>

    <el-dialog
      :title="`${localization('Import Strategies Cards')}`"
      :visible.sync="importDialogVisible"
      width="650px"
      top="5%"
      :close-on-click-modal="false"
      @close="resetImportDialog"
    >
      <p class="mb-3">
        {{
          localization("Upload an Excel file (.xlsx). Rows with Id update existing records; rows without Id create new ones.")
        }}
      </p>
      <p class="mb-4">
        {{
          localization("Columns: Id, classification, level1 (name), level2 (description), pattern, priority, Age Group, Language, Topic, Sub-Topic.")
        }}
      </p>

      <el-upload
        ref="importUpload"
        drag
        action="#"
        :auto-upload="false"
        :file-list="importFileList"
        :limit="1"
        accept=".xlsx,.xls,.csv"
        :on-change="handleImportFileChange"
        :on-remove="handleImportFileRemove"
        :http-request="() => {}"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">
          {{ localization("Drop file here or click to upload") }}
        </div>
        <div class="el-upload__tip" slot="tip">
          .xlsx, .xls, .csv
        </div>
      </el-upload>

      <span slot="footer" class="dialog-footer">
        <el-button @click="importDialogVisible = false">{{ localization("Cancel") }}</el-button>
        <el-button type="primary" :loading="importingFile" @click="submitImport">
          {{ localization("Import") }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      @close="resetForm"
      :title="isEdit ? localization('Update strategy card') : localization('Create new strategy card')"
      :visible.sync="dialogVisible"
      width="850px"
      top="1%"
      custom-class="task-modal global_modal"
      :close-on-click-modal="false"
    >
      <div class="custom_modal_header">
        <div class="images">
          <img src="@/assets/new-theme/icons/users-plus.png" alt="file" />
          <img
            @click="resetForm"
            class="close_img"
            src="@/assets/new-theme/x-close-modal.png"
            alt="file"
          />
        </div>
        <h3 class="title font-weight-bold mt-3">
          {{ isEdit ? localization("Update strategy card") : localization("Create new strategy card") }}
        </h3>
      </div>

      <el-form
        :rules="rules"
        ref="strategyCardForm"
        :model="form"
        label-position="top"
        @submit.native.prevent
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="`${localization('Classification')}*`" prop="classification" :error="getFieldError('classification')">
              <el-select
                v-model="form.classification"
                filterable
                class="w-100"
                :placeholder="`${localization('Select Classification')}`"
              >
                <el-option
                  v-for="item in classificationOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="`${localization('Pattern')}*`" prop="pattern" :error="getFieldError('pattern')">
              <el-select
                v-model="form.pattern"
                filterable
                class="w-100"
                :placeholder="`${localization('Select Pattern')}`"
              >
                <el-option
                  v-for="item in patternOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item :label="`${localization('Name')}*`" prop="name" :error="getFieldError('name')">
          <editor
            api-key="uwhpqwb5bkozdxtejm40jkuwvcy2u2kp98feem6n6y33fhzu"
            v-model="form.name"
            :init="editorNameInit"
          />
        </el-form-item>

        <el-form-item :label="`${localization('Description')}`" prop="description" :error="getFieldError('description')">
          <editor
            api-key="uwhpqwb5bkozdxtejm40jkuwvcy2u2kp98feem6n6y33fhzu"
            v-model="form.description"
            :init="editorInit"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item :label="`${localization('Priority')}`" prop="priority" :error="getFieldError('priority')">
              <el-input
                :placeholder="`${localization('Enter priority')}`"
                v-model="form.priority"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label="`${localization('Age Group')}`" prop="age_group" :error="getFieldError('age_group')">
              <el-select
                v-model="form.age_group"
                clearable
                filterable
                class="w-100"
                :placeholder="`${localization('Select Age Group')}`"
              >
                <el-option
                  v-for="item in ageGroupOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label="`${localization('Language')}`" prop="language_id" :error="getFieldError('language_id')">
              <el-select
                v-model="form.language_id"
                clearable
                filterable
                class="w-100"
                :placeholder="`${localization('Select Language')}`"
              >
                <el-option
                  v-for="item in languageOptions"
                  :key="item.id"
                  :label="getLanguageLabel(item)"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="`${localization('Topic')}*`" prop="topic_id" :error="getFieldError('topic_id')">
              <el-select
                v-model="form.topic_id"
                filterable
                class="w-100"
                :placeholder="`${localization('Select Topic')}`"
                @change="handleFormTopicChange"
              >
                <el-option
                  v-for="item in topicsCardLookups"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="`${localization('Subtopic')}*`" prop="subtopic_id" :error="getFieldError('subtopic_id')">
              <el-select
                v-model="form.subtopic_id"
                filterable
                class="w-100"
                :disabled="!form.topic_id"
                :placeholder="form.topic_id ? `${localization('Select Subtopic')}` : `${localization('Choose a Topic first')}`"
              >
                <el-option
                  v-for="item in formSubTopicOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div class="lci_modal_buttons mt-5">
        <button @click="resetForm" class="lci_btn_modal_cancel">
          {{ localization("Cancel") }}
        </button>
        <button @click="submitForm" class="lci_btn save lci_btn_modal_cancel" :disabled="loading">
          {{ isEdit ? localization("Update") : localization("Create") }}
        </button>
      </div>
    </el-dialog>

    <el-dialog
      :title="`${localization('Strategy Card Details')}`"
      :visible.sync="detailsDialogVisible"
      width="650px"
      top="5%"
      custom-class="task-modal global_modal"
    >
      <div v-if="selectedDetailsItem">
        <p><strong>{{ localization('Name') }}:</strong></p>
        <div class="mb-2" v-html="selectedDetailsItem.name || '-'"></div>
        <p><strong>{{ localization('Classification') }}:</strong> {{ selectedDetailsItem.classification || '-' }}</p>
        <p><strong>{{ localization('Pattern') }}:</strong> {{ selectedDetailsItem.pattern || '-' }}</p>
        <p><strong>{{ localization('Priority') }}:</strong> {{ selectedDetailsItem.priority || '-' }}</p>
        <p><strong>{{ localization('Age Group') }}:</strong> {{ selectedDetailsItem.age_group || '-' }}</p>
        <p><strong>{{ localization('Language') }}:</strong> {{ selectedDetailsItem.language_name || '-' }}</p>
        <p><strong>{{ localization('Topic') }}:</strong> {{ selectedDetailsItem.topic_name || '-' }}</p>
        <p><strong>{{ localization('Subtopic') }}:</strong> {{ selectedDetailsItem.subtopic_name || '-' }}</p>
        <p><strong>{{ localization('Description') }}:</strong></p>
        <div v-html="selectedDetailsItem.description || '-'"></div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import Editor from "@tinymce/tinymce-vue";
import ServerTable from "../../components/DataTablePagination.vue";

export default {
  name: "StrategiesCardIndex",
  components: {
    editor: Editor,
    ServerTable
  },
  data() {
    return {
      dialogVisible: false,
      detailsDialogVisible: false,
      isEdit: false,
      selectedId: null,
      selectedDetailsItem: null,
      lookupsLoaded: false,
      listLoaded: false,
      importDialogVisible: false,
      importFileList: [],
      importFile: null,
      importingFile: false,
      editorNameInit: {
        height: 160,
        mode: "none",
        menubar: false,
        dialog_type: "modal",
        importcss_append: true,
        relative_urls: false,
        remove_script_host: false,
        convert_urls: false,
        language: "en",
        zIndex: 10000,
        toolbar:
          "undo redo | bold italic underline | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | fullscreen code | numlist bullist | forecolor backcolor | ltr rtl",
        plugins: ["code"]
      },
      editorInit: {
        height: 260,
        mode: "none",
        menubar: false,
        dialog_type: "modal",
        importcss_append: true,
        relative_urls: false,
        remove_script_host: false,
        convert_urls: false,
        language: "en",
        zIndex: 10000,
        toolbar:
          "undo redo | bold italic underline | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | fullscreen code | numlist bullist | forecolor backcolor | ltr rtl",
        plugins: [
          "table charmap preview fullscreen importcss textpattern code"
        ]
      },
      languageOptions: [],
      formSubTopicOptions: [],
      filterSubTopicOptions: [],
      apiErrors: {},
      filter: {
        topic_id: "",
        subtopic_id: "",
        language_id: "",
        classification: "",
        pattern: "",
        age_group: ""
      },
      form: {
        classification: "",
        name: "",
        description: "",
        pattern: "",
        priority: "",
        age_group: "",
        language_id: null,
        topic_id: null,
        subtopic_id: null
      },
      patternOptions: [
        { label: "Confluence" },
        { label: "Precision" },
        { label: "Sequence" },
        { label: "Technical Reasoning" }
      ],
      classificationOptions: [
        { label: "Forge" },
        { label: "Intensify" },
        { label: "Tether" },
        { label: "Use First" }
      ],
      ageGroupOptions: [
        { label: "Grade 1" },
        { label: "Grade 2" },
        { label: "Grade 3" },
        { label: "Grade 4" },
        { label: "Grade 5" },

      ]
    };
  },
  computed: {
    ...mapGetters("strategies_card", [
      "strategiesCards",
      "totalStrategiesCards",
      "strategiesCard",
      "loading"
    ]),
    ...mapGetters("topics_card", ["topicsCardLookups"]),
    canAccessModule() {
      return this.hasPermission("access strategies_card") || this.hasPermission("view strategies_card");
    },
    canView() {
      return this.hasPermission("view strategies_card");
    },
    canCreate() {
      return this.hasPermission("create strategies_card");
    },
    canEdit() {
      return this.hasPermission("edit strategies_card");
    },
    canDelete() {
      return this.hasPermission("delete strategies_card");
    },
    canImport() {
      return this.canCreate || this.canEdit;
    },
    importActionPermission() {
      if (this.canCreate) {
        return "create strategies_card";
      }
      if (this.canEdit) {
        return "edit strategies_card";
      }
      return "access strategies_card";
    },
    importDropdownPermission() {
      if (this.hasPermission("view strategies_card")) {
        return "view strategies_card";
      }
      if (this.hasPermission("access strategies_card")) {
        return "access strategies_card";
      }
      return this.importActionPermission;
    },
    rules() {
      return {
        classification: [
          {
            required: true,
            message: this.localization("Please this field is required"),
            trigger: "change"
          }
        ],
        name: [
          {
            required: true,
            message: this.localization("Please this field is required"),
            trigger: "blur"
          }
        ],
        pattern: [
          {
            required: true,
            message: this.localization("Please this field is required"),
            trigger: "change"
          }
        ],
        topic_id: [
          {
            required: true,
            message: this.localization("Please this field is required"),
            trigger: "change"
          }
        ],
        subtopic_id: [
          {
            required: true,
            message: this.localization("Please this field is required"),
            trigger: "change"
          }
        ]
      };
    }
  },
  watch: {
    canAccessModule: {
      immediate: true,
      handler(value) {
        if (value && !this.lookupsLoaded) {
          this.fetchLookups();
        }
      }
    },
    canView: {
      immediate: true,
      handler(value) {
        if (value && !this.listLoaded) {
          this.Refresh({});
        }
      }
    }
  },
  methods: {
    Refresh(query) {
      if (!this.canView) {
        return;
      }

      if (arguments.length > 0 && query) {
        this.$store.dispatch("strategies_card/GetStrategiesCards", { query: this.prepareListQuery(query) });
      } else if (this.$refs.customTable) {
        this.$refs.customTable.Refresh();
      } else {
        this.$store.dispatch("strategies_card/GetStrategiesCards", { query: {} });
      }
      this.listLoaded = true;
    },
    prepareListQuery(query) {
      const newQuery = { ...query };
      if (newQuery.removed !== undefined) {
        newQuery.with_deleted = Number(newQuery.removed) === 1;
        delete newQuery.removed;
      }
      return newQuery;
    },
    resetFilters() {
      this.filter = {
        topic_id: "",
        subtopic_id: "",
        language_id: "",
        classification: "",
        pattern: "",
        age_group: ""
      };
      this.filterSubTopicOptions = [];
    },
    getFieldError(field) {
      const value = this.apiErrors[field];
      if (Array.isArray(value)) {
        return value[0];
      }
      return value || "";
    },
    getLanguageLabel(item) {
      return item.title || item.name || item.label || item.language || "";
    },
    clearApiErrors() {
      this.apiErrors = {};
    },
    normalizeSubTopics(response) {
      return Array.isArray(response)
        ? response
        : (response && (response.subTopicsCards || response.sub_topics_cards || response.data || [])) || [];
    },
    normalizeLanguages(response) {
      if (Array.isArray(response)) {
        return response;
      }

      if (!response || typeof response !== "object") {
        return [];
      }

      if (Array.isArray(response.languages)) {
        return response.languages;
      }

      if (response.data && Array.isArray(response.data.languages)) {
        return response.data.languages;
      }

      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      return [];
    },
    getErrorMessage(error) {
      return (error && error.response && error.response.data && error.response.data.message) || this.localization("Something went wrong");
    },
    openImportDialog() {
      if (!this.canImport) {
        return;
      }
      this.importDialogVisible = true;
    },
    resetImportDialog() {
      this.importFileList = [];
      this.importFile = null;
      this.importingFile = false;
      if (this.$refs.importUpload) {
        this.$refs.importUpload.clearFiles();
      }
    },
    handleImportFileChange(file, fileList) {
      this.importFileList = fileList.slice(-1);
      this.importFile = this.importFileList.length ? this.importFileList[0].raw : null;
    },
    handleImportFileRemove() {
      this.importFile = null;
      this.importFileList = [];
    },
    buildImportSummaryHtml(response) {
      const created = response.created || 0;
      const updated = response.updated || 0;
      const skipped = response.skipped || 0;
      const errors = Array.isArray(response.errors) ? response.errors : [];

      const summary = `<p style="margin-bottom: 12px;"><strong>${this.localization("Imported successfully")}:</strong> ${created} ${this.localization("created")}, ${updated} ${this.localization("updated")}, ${skipped} ${this.localization("skipped")}.</p>`;

      if (!errors.length) {
        return summary;
      }

      const errorsList = errors
        .map((item) => `<li style="margin-bottom: 6px;">${item}</li>`)
        .join("");

      return `${summary}
        <p style="margin: 10px 0 8px;"><strong>${this.localization("Rows with errors")}:</strong></p>
        <div style="max-height: 240px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px;">
          <ul style="padding-left: 18px; margin: 0;">${errorsList}</ul>
        </div>`;
    },
    submitImport() {
      if (!this.importFile) {
        Swal.fire({
          icon: "warning",
          text: this.localization("Please select a file to import."),
          confirmButtonClass: "btn btn-warning",
          buttonsStyling: false
        });
        return;
      }

      this.importingFile = true;
      this.$store
        .dispatch("strategies_card/ImportStrategiesCards", this.importFile)
        .then((response) => {
          const importResponse = response || {};
          Swal.fire({
            icon: "success",
            title: this.localization("Import Result"),
            html: this.buildImportSummaryHtml(importResponse),
            confirmButtonClass: "btn btn-success",
            buttonsStyling: false
          });
          this.importDialogVisible = false;
          this.resetImportDialog();
          this.Refresh();
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 403) {
            Swal.fire({
              icon: "error",
              text: this.localization("You do not have permission to import strategies cards."),
              confirmButtonClass: "btn btn-danger",
              buttonsStyling: false
            });
            return;
          }

          if (error && error.response && error.response.status === 422) {
            const apiError = error.response.data && error.response.data.error;
            const fileErrors = apiError && apiError.file ? apiError.file : [];
            const message = Array.isArray(fileErrors) && fileErrors.length ? fileErrors[0] : this.localization("Validation failed.");

            Swal.fire({
              icon: "warning",
              text: message,
              confirmButtonClass: "btn btn-warning",
              buttonsStyling: false
            });
            return;
          }

          Swal.fire({
            icon: "error",
            text: this.getErrorMessage(error),
            confirmButtonClass: "btn btn-danger",
            buttonsStyling: false
          });
        })
        .finally(() => {
          this.importingFile = false;
        });
    },
    async fetchLookups() {
      await this.$store.dispatch("topics_card/GetTopicsCardLookups");
      const langsResponse = await this.$store.dispatch("language/allLanguages");
      let options = this.normalizeLanguages(langsResponse);

      // Fallback to the shared language getter path used across the app.
      if (!options.length) {
        await this.$store.dispatch("language/getLanguages");
        options = this.$store.getters["language/languages"] || [];
      }

      this.languageOptions = options;
      this.lookupsLoaded = true;
    },
    async handleFormTopicChange(topicId) {
      this.form.subtopic_id = null;
      this.clearApiErrors();

      if (!topicId) {
        this.formSubTopicOptions = [];
        return;
      }

      const response = await this.$store.dispatch("sub_topics_card/GetSubTopicsCardLookups", { topic_card_id: topicId });
      this.formSubTopicOptions = this.normalizeSubTopics(response);
    },
    async handleFilterTopicChange(topicId) {
      this.filter.subtopic_id = "";
      if (!topicId) {
        this.filterSubTopicOptions = [];
        return;
      }
      const response = await this.$store.dispatch("sub_topics_card/GetSubTopicsCardLookups", { topic_card_id: topicId });
      this.filterSubTopicOptions = this.normalizeSubTopics(response);
    },
    openCreateModal() {
      if (!this.canCreate) {
        return;
      }
      this.isEdit = false;
      this.selectedId = null;
      this.resetForm();
      this.dialogVisible = true;
    },
    async openEditModal(row) {
      if (!this.canEdit) {
        return;
      }

      this.isEdit = true;
      this.selectedId = row.id;
      this.form = {
        classification: row.classification || "",
        name: row.name || "",
        description: row.description || "",
        pattern: row.pattern || "",
        priority: row.priority || "",
        age_group: row.age_group || "",
        language_id: row.language_id || null,
        topic_id: row.topic_id || null,
        subtopic_id: row.subtopic_id || null
      };
      this.clearApiErrors();

      if (this.form.topic_id) {
        const response = await this.$store.dispatch("sub_topics_card/GetSubTopicsCardLookups", { topic_card_id: this.form.topic_id });
        this.formSubTopicOptions = this.normalizeSubTopics(response);
      } else {
        this.formSubTopicOptions = [];
      }

      this.dialogVisible = true;
    },
    buildPayload() {
      return {
        classification: this.form.classification || null,
        name: this.form.name,
        description: this.form.description || null,
        pattern: this.form.pattern || null,
        priority: this.form.priority || null,
        age_group: this.form.age_group || null,
        language_id: this.form.language_id || null,
        topic_id: this.form.topic_id,
        subtopic_id: this.form.subtopic_id
      };
    },
    submitForm() {
      this.$refs.strategyCardForm.validate((valid) => {
        if (!valid) {
          return;
        }

        this.clearApiErrors();
        const action = this.isEdit
          ? "strategies_card/UpdateStrategiesCard"
          : "strategies_card/CreateStrategiesCard";
        const payload = this.isEdit
          ? { id: this.selectedId, form: this.buildPayload() }
          : this.buildPayload();

        this.$store
          .dispatch(action, payload)
          .then(() => {
            this.resetForm();
            Swal.fire({
              title: "",
              icon: "success",
              text: `${this.localization("Done")}`,
              confirmButtonText: `${this.localization("Ok")}`,
              type: "success",
              confirmButtonClass: "btn btn-success",
              buttonsStyling: false
            });
            this.Refresh();
          })
          .catch((error) => {
            if (error && error.response && error.response.status === 422) {
              this.apiErrors = (error.response.data && error.response.data.error) || {};
              return;
            }
            Swal.fire({
              title: "",
              icon: "error",
              text: this.getErrorMessage(error),
              confirmButtonText: `${this.localization("Ok")}`,
              confirmButtonClass: "btn btn-danger",
              buttonsStyling: false
            });
          });
      });
    },
    async showDetails(id) {
      if (!this.canView) {
        return;
      }
      try {
        await this.$store.dispatch("strategies_card/ShowStrategiesCard", id);
        this.selectedDetailsItem = this.strategiesCard;
        this.detailsDialogVisible = true;
      } catch (error) {
        Swal.fire({
          title: "",
          icon: "error",
          text: this.getErrorMessage(error),
          confirmButtonText: `${this.localization("Ok")}`,
          confirmButtonClass: "btn btn-danger",
          buttonsStyling: false
        });
      }
    },
    deleteStrategyCard(id) {
      if (!this.canDelete) {
        return;
      }

      Swal.fire({
        title: "",
        text: `${this.localization("Are You Sure")}`,
        type: "warning",
        icon: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger",
        confirmButtonText: `${this.localization("Delete")}`,
        cancelButtonText: `${this.localization("Cancel")}`,
        buttonsStyling: false
      }).then((result) => {
        if (result.isConfirmed) {
          this.$store.dispatch("strategies_card/DeleteStrategiesCard", id).then(() => {
            this.Refresh();
            Swal.fire({
              title: "",
              icon: "success",
              type: "success",
              text: `${this.localization("Deleted")}`,
              confirmButtonText: `${this.localization("Ok")}`,
              confirmButtonClass: "btn btn-success",
              buttonsStyling: false
            });
          });
        }
      });
    },
    restoreStrategyCard(id) {
      if (!this.canEdit) {
        return;
      }

      Swal.fire({
        title: "",
        text: `${this.localization("Are You Sure You Want To Restore This Item?")}`,
        type: "info",
        icon: "info",
        showCancelButton: true,
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger",
        confirmButtonText: `${this.localization("Restore")}`,
        cancelButtonText: `${this.localization("Cancel")}`,
        buttonsStyling: false
      }).then((result) => {
        if (result.isConfirmed) {
          this.$store.dispatch("strategies_card/RestoreStrategiesCard", id).then(() => {
            this.Refresh();
            Swal.fire({
              title: "",
              icon: "success",
              type: "success",
              text: `${this.localization("Restored")}`,
              confirmButtonText: `${this.localization("Ok")}`,
              confirmButtonClass: "btn btn-success",
              buttonsStyling: false
            });
          });
        }
      });
    },
    resetForm() {
      this.form = {
        classification: "",
        name: "",
        description: "",
        pattern: "",
        priority: "",
        age_group: "",
        language_id: null,
        topic_id: null,
        subtopic_id: null
      };
      this.selectedId = null;
      this.isEdit = false;
      this.formSubTopicOptions = [];
      this.clearApiErrors();
      this.dialogVisible = false;
      if (this.$refs.strategyCardForm) {
        this.$refs.strategyCardForm.clearValidate();
      }
    }
  },
  mounted() {
    // Initial call kept for normal fast path.
    if (this.canAccessModule && !this.lookupsLoaded) this.fetchLookups();
    if (this.canView && !this.listLoaded) this.Refresh({});
  }
};
</script>

<style lang="scss" scoped>
.main_profile_wrapper {
  background: #fff;
  width: 100%;
  max-width: 100%;
  padding: 0 30px 50px;
}

.main_table {
  margin-top: 30px;
  border-radius: 8px;
  border: 1px solid #eaecf0;

  .head_table {
    border-bottom: 1px solid #eaecf0;
  }
}

.filters_gap {
  gap: 10px;
}

.filter_select {
  width: 180px;
}

.restore_icon {
  width: 20px;
  height: 20px;
}

.lci_modal_buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3%;
  .lci_btn_modal_cancel {
    background-color: #fff;
    color: #667085;
    border-radius: 8px;
    padding: 12px 20px;
    border: 1px solid #eaecf0;
    width: calc(97% / 2);
    &.save {
      background-color: #7f56d9;
      color: #fff;
    }
  }
}
</style>

<style lang="scss">
.tox-tinymce-aux {
  z-index: 10000 !important;
}
</style>
